import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, Smartphone } from 'lucide-react';
import LoyaltyCard from '../components/loyalty/LoyaltyCard';
import axios from 'axios';
import { safeCapture } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AppleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return "À l'instant";
  if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
  if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)}h`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

const txLabel = (tx) => {
  const n = tx.points;
  if (tx.transaction_type === 'stamp') return n > 1 ? `+${n} tampons` : '+1 tampon';
  if (tx.transaction_type === 'redeem') {
    return tx.card_type === 'stamps'
      ? `${n} tampon${n > 1 ? 's' : ''} retiré${n > 1 ? 's' : ''}`
      : `${n} point${n > 1 ? 's' : ''} utilisé${n > 1 ? 's' : ''}`;
  }
  return `+${n} point${n > 1 ? 's' : ''}`;
};

const TransactionRow = ({ tx }) => (
  <li className="flex items-center justify-between gap-3 py-3" data-testid={`tx-${tx.id}`}>
    <span className={`text-[16px] font-semibold ${tx.transaction_type === 'redeem' ? 'text-[#4A515C]' : 'text-[#15171A]'}`}>
      {txLabel(tx)}
    </span>
    <span className="text-[14px] text-[#5D6470] shrink-0">{formatDate(tx.created_at)}</span>
  </li>
);

const WalletPassPage = () => {
  const { memberCode } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = searchParams.get('new') === 'true';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [loadingGoogle, setLoadingGoogle] = useState(null);
  const [error, setError] = useState('');
  // OPT-3 — track last failed action to allow targeted retry
  const [lastFailed, setLastFailed] = useState(null); // {type: 'apple'|'google', cardId, source}

  const isAndroid = useMemo(() => /android/i.test(navigator.userAgent), []);

  const fetchInfo = useCallback(async () => {
    try {
      const { data: resp } = await axios.get(`${API_URL}/api/public/wallet/info/${memberCode}`);
      setData(resp);
    } catch {
      setError('Carte de fidélité introuvable.');
    } finally {
      setLoading(false);
    }
  }, [memberCode]);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  // Funnel analytics — fire once per screen view when the customer payload
  // is loaded. Anonymous: NO customer_id / member_code / email / name —
  // only structural facts about the screen.
  useEffect(() => {
    if (!data?.customer?.id) return;
    safeCapture('wallet_screen_viewed', {
      is_new_signup: isNew,
      is_android: isAndroid,
      num_cards: Array.isArray(data.cards) ? data.cards.length : undefined,
    });
    // Intentional: depend on customer id only so we fire once per identity,
    // not on every re-render. isNew/isAndroid are stable per session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.customer?.id]);

  // OPT-2 — Silent prefetch: trigger pkpass generation in the background as
  // soon as the page loads. The user's click on "Add to Apple Wallet" then
  // hits a hot disk cache (~50ms) instead of paying the full cold-cache cost
  // (R2 logo + banner + PIL + PKCS7 = 1-3s). Errors are silenced — the click
  // will retry naturally.
  //
  // R4 — Gate behind `isNew` only. The /join flow already fires `_prewarm_pkpass`
  // server-side on signup, so a returning visitor doesn't need to pay the cost
  // again — and a NEW signup with hot prewarm doesn't need a 2nd parallel
  // generation. This eliminates double generate_pkpass on the same (member, balance).
  useEffect(() => {
    if (!isNew) return;
    if (!data || !data.cards || data.cards.length === 0 || isAndroid) return;
    const primaryCard = data.cards[0];
    const ctrl = new AbortController();
    axios.get(
      `${API_URL}/api/public/wallet/apple/${primaryCard.id}/${data.customer.id}`,
      { responseType: 'blob', timeout: 30000, signal: ctrl.signal }
    ).catch(() => { /* silent — the user click will retry if needed */ });
    return () => ctrl.abort();
  }, [data, isAndroid, isNew]);

  const downloadPass = async (cardId, source) => {
    // Funnel analytics — user intent (click on Apple Wallet button).
    safeCapture('wallet_button_clicked', {
      wallet: 'apple',
      card_id: cardId,
      source,
    });
    if (!data) return;
    setDownloading(cardId);
    setError('');
    setLastFailed(null);
    try {
      const resp = await axios.get(
        `${API_URL}/api/public/wallet/apple/${cardId}/${data.customer.id}`,
        { responseType: 'blob', timeout: 30000 }
      );
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'loyalty.pkpass';
      document.body.appendChild(a);
      // Funnel analytics — pkpass blob received, install is about to be
      // triggered. Skipped on the error path below (no event when backend
      // fails before this point).
      safeCapture('wallet_install_initiated', {
        wallet: 'apple',
        card_id: cardId,
        source,
      });
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setLastFailed({ type: 'apple', cardId, source });
      if (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')) {
        setError('La génération de votre carte prend plus de temps que prévu. Cliquez sur Réessayer.');
      } else {
        setError('Erreur lors du téléchargement. Cliquez sur Réessayer.');
      }
    } finally {
      setDownloading(null);
    }
  };

  const addToGoogleWallet = async (cardId, source) => {
    // Funnel analytics — user intent (click on Google Wallet button).
    safeCapture('wallet_button_clicked', {
      wallet: 'google',
      card_id: cardId,
      source,
    });
    if (!data) return;
    setLoadingGoogle(cardId);
    setError('');
    setLastFailed(null);
    try {
      const { data: resp } = await axios.get(
        `${API_URL}/api/public/wallet/google/${cardId}/${data.customer.id}`,
        { timeout: 30000 }
      );
      if (resp.save_url) {
        // Funnel analytics — Google save_url received, redirect is about
        // to fire. Skipped if backend returns no save_url (error branch).
        safeCapture('wallet_install_initiated', {
          wallet: 'google',
          card_id: cardId,
          source,
        });
        window.location.href = resp.save_url;
      } else {
        setError('Google Wallet non disponible.');
      }
    } catch (err) {
      setLastFailed({ type: 'google', cardId, source });
      if (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')) {
        setError('La génération prend plus de temps que prévu. Cliquez sur Réessayer.');
      } else {
        setError('Erreur Google Wallet. Cliquez sur Réessayer.');
      }
    } finally {
      setLoadingGoogle(null);
    }
  };

  const retryLast = () => {
    if (!lastFailed) return;
    if (lastFailed.type === 'apple') downloadPass(lastFailed.cardId, lastFailed.source);
    else if (lastFailed.type === 'google') addToGoogleWallet(lastFailed.cardId, lastFailed.source);
  };

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-[#EEF0F2] px-4 pt-6">
        <div className="max-w-md mx-auto space-y-4" aria-busy="true">
          <div className="h-8 w-2/3 rounded-lg bg-[#DCE0E5] animate-pulse" />
          <div className="h-[260px] rounded-[22px] bg-[#DCE0E5] animate-pulse" />
          <div className="h-14 rounded-2xl bg-[#DCE0E5] animate-pulse" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-[100dvh] bg-[#EEF0F2] flex items-center px-6">
        <div className="max-w-md mx-auto">
          <h1 className="text-[26px] font-bold text-[#15171A] mb-3">Carte introuvable</h1>
          <p className="text-[17px] leading-relaxed text-[#4A515C]" data-testid="wallet-error">
            Ce lien ne correspond à aucune carte. Demandez au commerce de vous renvoyer le lien de votre carte.
          </p>
        </div>
      </div>
    );
  }

  const displayCards = data.cards.slice(0, 3);
  const firstName = (data.customer.name || '').split(' ')[0];
  const recent = (data.transactions || []).slice(0, 5);

  const walletButtons = (card, source) => (
    <div className="space-y-3 mt-4" data-testid={source === 'hero' ? 'hero-wallet-buttons' : undefined}>
      {!isAndroid && (
        <button
          onClick={() => downloadPass(card.id, source)}
          disabled={downloading === card.id}
          className="w-full h-14 rounded-2xl bg-black text-white text-[17px] font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-70 focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-[#15171A]"
          data-testid={source === 'hero' ? 'hero-apple-wallet-btn' : `apple-wallet-btn-${card.id}`}
        >
          {downloading === card.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <AppleIcon className="w-5 h-5" />}
          {downloading === card.id ? 'Préparation de la carte…' : 'Ajouter à Apple Wallet'}
        </button>
      )}
      {data.google_wallet_configured && (
        <button
          onClick={() => addToGoogleWallet(card.id, source)}
          disabled={loadingGoogle === card.id}
          className={`w-full h-14 rounded-2xl text-[17px] font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-70 focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-[#15171A] ${
            isAndroid ? 'bg-black text-white' : 'bg-white text-[#15171A] border border-[#D5D9DF]'
          }`}
          data-testid={source === 'hero' ? 'hero-google-wallet-btn' : `google-wallet-btn-${card.id}`}
        >
          {loadingGoogle === card.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <Smartphone className="w-5 h-5" />}
          Ajouter à Google Wallet
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-[100dvh] bg-[#EEF0F2]" data-testid="wallet-pass-page">
      {error && lastFailed && (
        <div className="bg-[#FDECEA] px-4 py-3 flex items-center justify-between gap-3" role="alert" data-testid="wallet-retry-banner">
          <p className="text-[#8A1F17] text-[15px] flex-1" data-testid="wallet-retry-message">{error}</p>
          <button
            onClick={retryLast}
            className="bg-[#15171A] text-white text-sm font-semibold px-4 py-2 rounded-lg flex-shrink-0"
            data-testid="wallet-retry-button"
          >
            Réessayer
          </button>
        </div>
      )}

      <div className="max-w-md mx-auto px-4 pt-6 pb-10">
        <header className="px-1 mb-5" data-testid="wallet-hero">
          {data.merchant_name && <p className="text-[15px] font-semibold text-[#4A515C] mb-1">{data.merchant_name}</p>}
          <h1 className="text-[28px] leading-tight font-bold text-[#15171A]" data-testid="wallet-hero-title">
            {isNew ? 'Votre carte est prête' : `Bonjour ${firstName}`}
          </h1>
          <p className="text-[16px] leading-snug text-[#4A515C] mt-2" data-testid="wallet-hero-subtitle">
            {isNew
              ? 'Ajoutez-la à votre téléphone. Elle se met à jour toute seule à chaque passage.'
              : 'Voici où vous en êtes.'}
          </p>
        </header>

        {displayCards.length === 0 && (
          <p className="text-[16px] text-[#4A515C] bg-white rounded-2xl p-5">
            Vous n’avez pas encore de carte active chez {data.merchant_name || 'ce commerce'}.
          </p>
        )}

        <div className="space-y-8">
          {displayCards.map((card, i) => (
            <div key={card.id}>
              <LoyaltyCard card={card} balance={card.balance} title={data.customer.name} />
              {walletButtons(card, i === 0 ? 'hero' : 'card')}
            </div>
          ))}
        </div>

        {isNew && displayCards.length > 0 && (
          <section className="mt-10 bg-white rounded-[22px] p-5">
            <h2 className="text-[19px] font-bold text-[#15171A] mb-4">Comment ça marche</h2>
            <ol className="space-y-3 text-[16px] leading-snug text-[#15171A]">
              <li className="flex gap-3"><span className="font-bold tabular-nums">1.</span>Ajoutez la carte à votre téléphone avec le bouton ci-dessus.</li>
              <li className="flex gap-3"><span className="font-bold tabular-nums">2.</span>À chaque passage, montrez-la en caisse.</li>
              <li className="flex gap-3"><span className="font-bold tabular-nums">3.</span>Vos {displayCards[0].card_type === 'stamps' ? 'tampons' : 'points'} s’ajoutent tout seuls. Vous êtes prévenu quand la récompense est prête.</li>
            </ol>
          </section>
        )}

        {recent.length > 0 && (
          <section className="mt-10">
            <h2 className="text-[19px] font-bold text-[#15171A] mb-1 px-1">Vos derniers passages</h2>
            <ul className="bg-white rounded-[22px] px-5 divide-y divide-[#EEF0F2]">
              {recent.map((tx) => <TransactionRow key={tx.id} tx={tx} />)}
            </ul>
          </section>
        )}

        <footer className="mt-10 text-center text-[13px] text-[#5D6470] space-y-1">
          <p>Votre code client : <span className="font-semibold tabular-nums whitespace-nowrap" data-testid="member-code">{data.customer.member_code}</span></p>
          <p>Propulsé par LoyalPass</p>
        </footer>
        <span className="sr-only" data-testid="customer-name">{data.customer.name}</span>
      </div>
    </div>
  );
};

export default WalletPassPage;
